import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * The list that is open on a dashboard, kept in the address as ?list=<slug>.
 *
 * One mechanism for everything that opens a list: a headline number, a tab or strip
 * button, the home screen's rows and page search. Because the open list is the address,
 * it can be linked to, survives a refresh, and Back closes it.
 *
 * A tab must DERIVE what is open from `list` on every render (open when the slug is
 * present and its data has arrived). Never copy it once into component state: several
 * tabs refetch a moment after opening, when the default Role filter lands, and a list
 * opened once on arrival would be shut again.
 *
 * Opening and closing REPLACE the address rather than adding a step to the browser's
 * Back, and leave every other value in the query as it was.
 */
export function useDashboardList(): {
  /** The slug in the address, or null. A tab ignores a slug it does not know. */
  list: string | null;
  openList: (slug: string) => void;
  closeList: () => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const list = searchParams.get('list');

  const openList = useCallback((slug: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('list', slug);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const closeList = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('list');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  return useMemo(() => ({ list, openList, closeList }), [list, openList, closeList]);
}
